import { Col, Row } from "react-bootstrap"
import Pane from "../General/Pane"
import styles from "./Student.module.scss"
import { toast } from "react-toastify"
import { StudentAssignmentResponse } from "shared/types/studentTypes"
import { formatToLongDate } from "helpers/dateHelper"
import { beforeCurrentDay, dueSoon } from "helpers/dateHelper"
import { getStudentSubmissionId, getTopicSubmissionId } from "helpers/getSubmissionId"
import { useNavigate } from "react-router"
import { HourglassSplit, HourglassBottom, CalendarWeek, Calendar } from "react-bootstrap-icons"
import { AssignmentResponse } from "shared/types/teacherTypes"
import { useContext } from "react"
import { AuthContext } from "AuthContext"
import lessons from "shared/lessons"
import { TeacherGradesState } from "components/Lessons/Grading/GradeList/TeacherGrades"
import ToolTip from "components/General/ToolTip"
import { getSubmissions } from "shared/routes/assignments"
import Paginator from "../General/Pagination/Paginator"
import { isStudentAssignment } from "shared/typeGuards/isStudentAssignment"
import { StudentAssignmentStateProps } from "shared/types/moduleTypes"
import { AssignmentPreviewStateProps } from "components/Lessons/Grading/AssignmentPreview/Preview"
import { toProductString } from "helpers/blockStringConverter"

interface Props {
    filteredAssignments: StudentAssignmentResponse[] | AssignmentResponse[]
}

const AssignmentsOverview = ({ filteredAssignments } : Props) => {
    const navigate = useNavigate()
    const { isStudent } = useContext(AuthContext)
    
    const openAssignment = async (assignment: StudentAssignmentResponse | AssignmentResponse) => {
        if (isStudentAssignment(isStudent, assignment)) {
            try {
                const recentSubmission = (await getSubmissions({ assignmentId: assignment.id })).data?.[0]

                let studentSubmissionId = null
                let startFromAssessment = false
                const reworkAssignmentMode = recentSubmission?.status === "rework-required" || recentSubmission?.status === "reworking"

                if (assignment.lessonVariety === "Review") {
                    navigate("/dashboard/student/reviews", { state: { assignmentId: assignment.id }})
                    return
                } else if (assignment?.topicId) {
                    studentSubmissionId = await getTopicSubmissionId({assignment: assignment, recentSubmission: recentSubmission})
                } else {
                    studentSubmissionId = await getStudentSubmissionId({ assignment: assignment as StudentAssignmentResponse, recentSubmission: recentSubmission })
                    startFromAssessment = (recentSubmission?.lessonSubmittedDate && recentSubmission?.assessmentGrade === null && recentSubmission?.lessonGrade == null) ? true : false
                }
                navigate("/dashboard/student/assignments/assignment", {
                    state: {
                        submissionId: studentSubmissionId,
                        assignment: assignment,
                        continueAssessment: startFromAssessment,
                        reworkAssignmentMode: reworkAssignmentMode,
                        isReview: false,
                    } as StudentAssignmentStateProps
                })
            } catch (err) {
                toast.error(err?.response?.data?.message ?? "Unable to start assignment, please try again.")
            }
        } else {
            if (assignment.lessonVariety === "Guided" || assignment.lessonVariety === "Topic") {
                if(assignment.lessonId) {
                    var lesson = await lessons.findById(assignment.lessonId)
                }
                const TeacherGradesState: TeacherGradesState = {
                    assignment: assignment,
                    lesson: lesson
                }
                navigate("/dashboard/teacher/assignments/grading", {
                    state: TeacherGradesState
                })
            } else if (assignment.lessonVariety === "Review") {
                navigate(`/dashboard/teacher/science/${toProductString(assignment.block)}/assignments/preview`, {
                    state: {
                        assignment: assignment,
                        origin: "assignments",
                        blockType: assignment.block,
                    } as AssignmentPreviewStateProps
                })
            } else if (assignment.lessonVariety === "Pure Inquiry") {
                const TeacherGradesState: TeacherGradesState = {
                    assignment: assignment  
                }
                navigate("/dashboard/teacher/assignments/grading", {
                    state: TeacherGradesState
                })
            }
        }
    }

    const renderAssignment = (assignment: StudentAssignmentResponse | AssignmentResponse, index: number): React.ReactNode => {
        const isDueSoon = dueSoon(assignment.dueDate)
        const isPastDue = beforeCurrentDay(assignment.dueDate)
        const reworkRequired = isStudentAssignment(isStudent, assignment) ? assignment.status === "Rework Required" : false
        const rejected = isStudentAssignment(isStudent, assignment) ? assignment.status === "Rejected" : false

        const topicType = !!assignment.topicId
        const lessonType = !topicType && !!assignment.lessonId

        const tooltipTitle = isStudentAssignment(isStudent, assignment) ? (assignment?.status === "Incomplete" ? "Resume assignment" : "Start assignment") : (assignment.lessonVariety === "Review" ? "View assignment" : "Grade assignment")

        return <ToolTip title={tooltipTitle} key={assignment.id + index}>
            <div key={assignment.id} onClick={() => openAssignment(assignment)}>
                <Pane className={`shadow-sm mt-2 ${styles.assignmentOverview}`}>
                    <Row>
                        <Col className="text-start" sm={12} md={7}>
                            <p className={`fw-bold ${styles.assignmentTitle}`}>
                                {assignment.title}
                                <span className={`rounded ms-2 px-2 ${topicType ? styles.labelTagTopic : lessonType ? styles.labelTagLesson : styles.labelTagAssessment}`}>
                                    {topicType ? "topic" : (lessonType ? "lesson" : assignment.lessonVariety === "Review" ? "review" : (assignment.lessonVariety === "Pure Inquiry" ? "pure inquiry" : "assessment"))}
                                </span>
                            </p>
                            <p className="text-muted fs-6 fw-bold mb-0">{assignment.moduleName}</p>
                        </Col>
                        <Col className={`text-start ${styles.dueDateContainer}`} sm={12} md={5}>
                            {assignment.dueDate && (
                                <>
                                    <p className={`m-0`}>
                                        <span className="fw-bold">Due:&nbsp;</span><span className={`text-success ${isPastDue && `text-danger`}`}>{formatToLongDate(assignment.dueDate)}</span>
                                    </p>
                                    {(isDueSoon || isPastDue) && 
                                        <p className={`${styles.dueStatus} ${isDueSoon && `bg-success`} ${isPastDue && `bg-danger`}
                                            fw-bold text-nowrap text-white mb-0 px-3 py-1 rounded text-center fw-bold d-inline-block mt-1`}>
                                            {isDueSoon && <><HourglassSplit className="me-2" /> due soon</>}
                                            {isPastDue && <><HourglassBottom className="me-2" /> past due</>}
                                        </p>
                                    }
                                </>)}
                            {reworkRequired &&
                                    <p className={`${styles.dueStatus} bg-info fw-bold rounded d-inline-block p-1 mb-0 ${assignment.dueDate ? "mt-2 ms-1" : "mt-0"}`}><i className={`fas fa-repeat`}/>&nbsp;Rework&nbsp;Required</p>
                                }

                            {rejected &&
                                    <p className={`${styles.dueStatus} bg-danger text-white fw-bold rounded d-inline-block p-1 mb-0 ${assignment.dueDate ? "mt-2 ms-1" : "mt-0"}`}><i className={`fas fa-times`}/>&nbsp;Rejected</p>
                                }
                        </Col>
                    </Row>
                </Pane>
            </div>
        </ToolTip>
    }

     if (!filteredAssignments || filteredAssignments.length === 0) {
        return <h5 className="text-center fw-bold text-muted my-3"><Calendar className="me-2 text-primary" /> No upcoming assignments</h5>
    }

  return (
    <div className={`p-2 mt-4`}>
        <h3 className="text-start ms-3 mb-4 fw-bold">
            <CalendarWeek className="me-2 text-primary" />
            Upcoming Class Assignments
        </h3>

        <Paginator items={filteredAssignments} renderItem={renderAssignment} position="down" />
    </div>
  )
}

export default AssignmentsOverview
